export const APP_ROUTES = Object.freeze({
  LANDING: 'landing',
  SESSION: 'session',
});

function normalizeHash(hash = '') {
  return hash.replace(/^#/, '').replace(/^\//, '');
}

export function parseAppRoute(hash = '') {
  const normalized = normalizeHash(hash);
  if (!normalized) {
    return { type: APP_ROUTES.LANDING };
  }

  const [segment, id] = normalized.split('/');
  if (segment === 's' && id) {
    return {
      type: APP_ROUTES.SESSION,
      sessionId: decodeURIComponent(id),
    };
  }

  return { type: APP_ROUTES.LANDING };
}

export function getCurrentAppRoute() {
  if (typeof window === 'undefined') {
    return { type: APP_ROUTES.LANDING };
  }
  return parseAppRoute(window.location.hash);
}

export function navigateToLanding() {
  if (typeof window === 'undefined') return;
  window.location.hash = '/';
}

export function navigateToSession(sessionId) {
  if (typeof window === 'undefined' || !sessionId) return;
  window.location.hash = `/s/${encodeURIComponent(sessionId)}`;
}

/**
 * The focused edit view's history entry: state only, on the session's own URL, so no hashchange
 * fires and the route above never learns about it. Its one writer is `useFocusedEditHistoryEntry`,
 * which projects the edit-session fact and steps back off the entry when the session ends.
 */
export const FOCUSED_EDIT_HISTORY_STATE = 'glowghostFocusedEdit';

export function pushFocusedEditHistoryEntry(sessionId) {
  if (typeof window === 'undefined' || !sessionId) return;
  window.history.pushState({ [FOCUSED_EDIT_HISTORY_STATE]: sessionId }, '', window.location.href);
}

export function isFocusedEditHistoryState(state, sessionId) {
  return Boolean(sessionId) && state?.[FOCUSED_EDIT_HISTORY_STATE] === sessionId;
}

/**
 * The one boundary every route exit from a session passes through: menu actions, browser Back and
 * Forward, and edits to the address bar all change the hash, so the route owner awaits this before
 * it unmounts the workspace. The mounted workspace registers its persistence exit step here;
 * `false` means the exit must not happen.
 */
let sessionExitGuard = null;

/**
 * `guard` starts one exit and resolves to `{ approved, end }`; `end` closes that exit when it is
 * not going to happen after all (an approved exit that unmounts the workspace needs no `end`).
 */
export function registerSessionExitGuard(guard) {
  sessionExitGuard = guard;
  return () => {
    if (sessionExitGuard === guard) sessionExitGuard = null;
  };
}

const NO_EXIT_TO_ABANDON = () => {};

/** @returns {Promise<{ allowed: boolean, abandon: () => void }>} */
export async function runSessionExitGuard() {
  if (!sessionExitGuard) return { allowed: true, abandon: NO_EXIT_TO_ABANDON };
  const exit = await sessionExitGuard();
  return { allowed: exit.approved, abandon: exit.end };
}
